import { useAppSelector } from "@/app/hooks";
import CBRELogo from "@/assets/images/cbreLogo.svg?react";
import { SurveySection } from "@/features/survey/types";
import MarketSwitcher from "@/shared/components/MarketSwitcher";
import PdfDrawer from "@/shared/components/PdfDrawer";
import { EmeraldIcon } from "@emerald-react/icon";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import DropdownMenu from "./components/DropdownMenu";
import MobilePropertiesMenu from "./components/MobilePropertiesMenu";
import { useMenuOverflow } from "./hooks/useMenuOverflow";
import { getCurrentPageTitle, getPdfUrl } from "./utils/menuHelpers";

interface MenuItem {
  path: string;
  label: string;
  isCustom?: boolean;
  section?: SurveySection;
  order?: number;
  name?: string;
}

interface InteractiveSurveyMenuBarProps {
  menuItems: MenuItem[];
  hideBranding?: boolean;
}

/**
 * Interactive Survey Menu Bar
 * Navigation menu for authenticated survey sections
 */
const InteractiveSurveyMenuBar = ({
  menuItems,
  hideBranding = false,
}: InteractiveSurveyMenuBarProps) => {
  const { surveyId, propertyId } = useParams<{
    surveyId: string;
    propertyId: string;
  }>();
  const location = useLocation();
  const { currentSurvey, mapFilters, currentSurveyGroup, groupSurveys } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const [isCustomSectionsDrawerOpen, setIsCustomSectionsDrawerOpen] =
    useState(false);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [isShortlistedOnly, setIsShortlistedOnly] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const {
    navRef,
    leftNavRef,
    rightNavRef,
    cbreLogoRef,
    updatedMenuItems,
    overflowSections,
  } = useMenuOverflow(menuItems);
  const getActiveMenuItem = (path: string) => {
    return location.pathname === path || location.pathname.includes(path);
  };

  // Sync local state with Redux state
  useEffect(() => {
    setIsShortlistedOnly(mapFilters.showShortlistOnly);
  }, [mapFilters.showShortlistOnly]);

  const pageTitle = getCurrentPageTitle({
    updatedMenuItems,
    propertyId,
    properties: currentSurvey?.properties,
    pathname: location.pathname,
    isActiveMenuItem: getActiveMenuItem,
  });

  const isOnPropertyPage =
    location.pathname.includes("/property/") && propertyId;

  const filteredProperties =
    currentSurvey?.properties?.filter((property) => {
      if (property.isHidden) return false;
      if (property.isCurrentLocation && !currentSurvey.showCurrentLocationPD)
        return false;
      if (mapFilters.showShortlistOnly && !property.isClientShortlist)
        return false;
      return true;
    }) ?? [];

  const handleDrawerClose = () => {
    setIsCustomSectionsDrawerOpen(false);
  };

  const handleClosePdfDrawer = () => {
    setIsPdfDrawerOpen(false);
  };

  const handleToggleShortlisted = (value: boolean) => {
    setIsShortlistedOnly(value);
  };

  if (!surveyId) return null;

  return (
    <>
      <header className={`main-nav ${hideBranding ? "main-nav--light" : ""}`} ref={navRef}>
        <div
          className="flex flex-col lg:flex-row items-center"
          ref={leftNavRef}
        >
          <div ref={cbreLogoRef}>
            {!hideBranding && (
              <Link to={`/interactive/${surveyId}/cover`}>
                <CBRELogo className="w-24 h-6.5" />
              </Link>
            )}
          </div>
          {!hideBranding && <div className="header-divider"></div>}
          {currentSurveyGroup && (
            <MarketSwitcher
              surveys={groupSurveys ?? []}
              currentSurveyId={surveyId}
              variant={hideBranding ? "light" : "dark"}
            />
          )}

          <div className="hidden lg:block desktop-nav-container">
            <div className="flex items-baseline nav-items">
              {(updatedMenuItems.length > 0
                ? updatedMenuItems
                : menuItems
              )?.map((item) => {
                // Special handling for Properties menu item
                if (item.label === "Properties") {
                  // Build children array from filteredProperties
                  const propertiesWithChildren = {
                    ...item,
                    children: filteredProperties.map((property) => ({
                      path: `/interactive/${surveyId}/property/${property.id}`,
                      label:
                        property.propertyName ||
                        property.name ||
                        `Property ${property.id}`,
                    })),
                  };
                  return (
                    <div key={item.path}>
                      <DropdownMenu item={propertiesWithChildren} />
                    </div>
                  );
                }

                // Handle custom sections with PDFs
                if (item.isCustom && item.section) {
                  const pdfUrl =
                    item.section.hasPDF && item.section.pdfs?.length
                      ? getPdfUrl(item.section.pdfs[0])
                      : "";
                  return (
                    <a
                      key={item.section.surveySectionId}
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`nav-item ${
                        getActiveMenuItem(item?.path)
                          ? "text-white underline"
                          : "text-white"
                      }`}
                    >
                      {item.label}
                    </a>
                  );
                }

                // Regular menu items
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${
                      getActiveMenuItem(item?.path) ? "active" : null
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {overflowSections.length > 0 && (
                <DropdownMenu
                  item={{
                    path: "",
                    label: "More",
                    children: overflowSections.map((section) => {
                      // Add children to Properties section in More menu
                      if (section.label === "Properties") {
                        return {
                          ...section,
                          children: filteredProperties.map((property) => ({
                            path: `/interactive/${surveyId}/property/${property.id}`,
                            label:
                              property.propertyName ||
                              property.name ||
                              `Property ${property.id}`,
                          })),
                        };
                      }
                      return section;
                    }),
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="lg:flex items-stretch hidden" ref={rightNavRef}>
          {!hideBranding && currentSurvey?.isClientPdfDownloadEnabled && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsPdfDrawerOpen(true);
              }}
              className="nav-item"
            >
              <EmeraldIcon icon="download" type="outlined" variant="symbol" />
              <span>PDF</span>
            </a>
          )}

          {!hideBranding && (
            <a
              href="https://www.cbre.com/about-us/disclaimer-terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-item"
            >
              Disclaimer
            </a>
          )}
        </div>
        <div className="lg:hidden flex gap-x-2 items-center min-w-0 flex-1 justify-end">
          <div className="lg:hidden mobile-properties-container min-w-0 flex-1">
            {isOnPropertyPage ? (
              <DropdownMenu
                position="bottom_end"
                item={{
                  path: `/interactive/${surveyId}/property/`,
                  label: "Properties",
                  children: filteredProperties.map((property) => ({
                    path: `/interactive/${surveyId}/property/${property.id}`,
                    label:
                      property.propertyName ||
                      property.name ||
                      `Property ${property.id}`,
                  })),
                }}
              />
            ) : (
              <p
                className={`text-base font-medium truncate ${hideBranding ? "text-[#13211f]" : "text-white"}`}
              >
                {pageTitle}
              </p>
            )}
          </div>
          {/* Mobile menu toggle button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden inline-flex items-center justify-center p-2 rounded-md focus:outline-none flex-shrink-0 ${
              hideBranding
                ? "text-[#5b6b69] hover:text-[#13211f]"
                : "text-gray-200 hover:text-green-300"
            }`}
          >
            <span className="sr-only">
              {isMobileMenuOpen ? "Close menu" : "Open menu"}
            </span>
            <EmeraldIcon icon={isMobileMenuOpen ? "close" : "menu"} />
          </button>
        </div>
      </header>
      {/* Mobile menu — offset below the group tab strip too, so its header (and close button) stay visible above the overlay */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden fixed inset-0 z-150 ${hideBranding ? "top-36" : "top-18"}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 w-screen h-screen overflow-auto sm:px-3 bg-cbre-green">
            {menuItems?.map((item) => {
              // Special handling for Properties menu item in mobile
              if (item.label === "Properties") {
                return (
                  <MobilePropertiesMenu
                    key={item.path}
                    surveyId={surveyId!}
                    properties={filteredProperties}
                    allProperties={currentSurvey?.properties}
                    isActive={getActiveMenuItem(item.path)}
                    showShortlistOnly={mapFilters.showShortlistOnly}
                    onItemClick={() => setIsMobileMenuOpen(false)}
                  />
                );
              }

              // Handle custom sections with PDFs in mobile
              if (item.isCustom && item.section) {
                const pdfUrl =
                  item.section.hasPDF && item.section.pdfs?.length
                    ? getPdfUrl(item.section.pdfs[0])
                    : "";
                return (
                  <a
                    key={item.section.surveySectionId}
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
                  >
                    {item.label}
                  </a>
                );
              }

              // Regular mobile menu items
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-sm font-normal transition-colors ${
                    getActiveMenuItem(item?.path)
                      ? "text-white underline font-semibold"
                      : "text-gray-200 hover:text-green-300"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {!hideBranding && currentSurvey?.isClientPdfDownloadEnabled && (
              <a
                href="#"
                className="px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors flex items-center space-x-2"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPdfDrawerOpen(true);
                }}
              >
                <EmeraldIcon icon="download" type="outlined" variant="symbol" />
                <span>PDF</span>
              </a>
            )}

            {!hideBranding && (
              <a
                href="https://www.cbre.com/about-us/disclaimer-terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
              >
                Disclaimer
              </a>
            )}
          </div>
        </div>
      )}
      {/* Custom Sections Drawer */}
      {isCustomSectionsDrawerOpen && (
        <div className="fixed inset-0 z-99">
          <div
            onClick={handleDrawerClose}
            className={`absolute inset-0 bg-opacity-10 transition-opacity duration-300 ${
              isCustomSectionsDrawerOpen ? "opacity-100" : "opacity-0"
            }`}
          />

          <div
            className={`absolute right-0 top-0 h-full w-[200px] bg-cbre-green shadow-2xl transform transition-transform duration-300 ease-in-out ${
              isCustomSectionsDrawerOpen ? "translate-x-0" : "translate-x-full"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-1">
                  {overflowSections.map((item) => {
                    const pdfUrl =
                      item.section?.hasPDF && item.section.pdfs?.length
                        ? getPdfUrl(item.section.pdfs[0])
                        : "";
                    return (
                      <a
                        key={item.section?.surveySectionId}
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleDrawerClose}
                        className="block px-3 py-2 font-normal transition-colors text-gray-200 hover:text-green-300 cursor-pointer rounded-md"
                      >
                        {item.label}
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Drawer Component */}
      <PdfDrawer
        isOpen={isPdfDrawerOpen}
        isShortlistedOnly={isShortlistedOnly}
        onClose={handleClosePdfDrawer}
        shortListToggleStatus={handleToggleShortlisted}
      />
    </>
  );
};

export default InteractiveSurveyMenuBar;
